import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { pdf } from "@react-pdf/renderer";
import JamaVoucherPDF from "./JamaVoucherPDF";

/**
 * Preview modal for Credit / Debit voucher PDF.
 *
 * Renders the PDF to a blob once, then shows a native <iframe>.
 * Avoids @react-pdf PDFViewer, which rebuilds on every React re-render
 * (e.g. when returning from another browser tab) and blanks the preview.
 */
function JamaVoucherPreviewModal({ show, onHide, voucherData }) {
  const title = voucherData?.title || "Credit Voucher";
  const [viewerHeight, setViewerHeight] = useState(640);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const urlRef = useRef(null);
  const builtForKeyRef = useRef(null);
  const generationIdRef = useRef(0);
  const voucherDataRef = useRef(voucherData);
  voucherDataRef.current = voucherData;

  const voucherKey = useMemo(() => {
    if (!voucherData) return "";
    const bills = Array.isArray(voucherData.allocations)
      ? voucherData.allocations
          .map(
            (bill) =>
              `${bill?.bill_no || ""}:${bill?.allocated_amount || ""}:${bill?.due_before || ""}:${bill?.due_after || ""}`,
          )
          .join(",")
      : "";
    return [
      voucherData.title,
      voucherData.voucherNo,
      voucherData.amount,
      voucherData.partyName,
      voucherData.balance,
      voucherData.outstanding,
      voucherData.paymentLabel,
      voucherData.voucherDate,
      voucherData.remarks,
      voucherData.partyAddress,
      voucherData.partyMobile,
      voucherData.isJama ? "jama" : "",
      voucherData.showBalance === false ? "nobals" : "",
      bills,
    ].join("|");
  }, [voucherData]);

  const revokeUrl = () => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  };

  useEffect(() => {
    if (!show || !voucherKey) {
      return undefined;
    }

    // Already built for this voucher — do not rebuild (tab focus / parent re-render)
    if (builtForKeyRef.current === voucherKey && urlRef.current) {
      return undefined;
    }

    const h = Math.max(480, Math.floor(window.innerHeight * 0.78));
    setViewerHeight(h);

    const generationId = ++generationIdRef.current;
    let cancelled = false;

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const data = voucherDataRef.current;
        if (!data) return;

        const blob = await pdf(<JamaVoucherPDF voucherData={data} />).toBlob();
        if (cancelled || generationId !== generationIdRef.current) return;

        revokeUrl();
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        builtForKeyRef.current = voucherKey;
        setPdfUrl(url);
      } catch (err) {
        if (cancelled || generationId !== generationIdRef.current) return;
        console.error("Jama voucher PDF render failed:", err);
        setError(err?.message || "Failed to generate PDF");
        setPdfUrl(null);
        builtForKeyRef.current = null;
      } finally {
        if (!cancelled && generationId === generationIdRef.current) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, voucherKey]);

  // Revoke blob only when modal fully closes
  useEffect(() => {
    if (show) return undefined;
    revokeUrl();
    setPdfUrl(null);
    setError(null);
    setLoading(false);
    builtForKeyRef.current = null;
    generationIdRef.current += 1;
    return undefined;
  }, [show]);

  // Safety: revoke on unmount
  useEffect(() => () => revokeUrl(), []);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      dialogClassName="pdf-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{title} Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body
        className="p-0"
        style={{ height: viewerHeight, overflow: "hidden" }}
      >
        {loading && !pdfUrl ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: viewerHeight }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading PDF...</span>
            </div>
          </div>
        ) : error ? (
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ height: viewerHeight }}
          >
            <h5 className="text-danger">Unable to load voucher</h5>
            <p className="text-muted small mb-0">{error}</p>
          </div>
        ) : pdfUrl ? (
          <iframe
            title={`${title} Preview`}
            src={pdfUrl}
            style={{
              width: "100%",
              height: viewerHeight,
              border: "none",
              display: "block",
            }}
          />
        ) : (
          <div className="text-center p-4">
            <h5 className="text-muted">No PDF Data Available</h5>
            <p className="text-muted">Unable to load voucher data</p>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default JamaVoucherPreviewModal;
